<template>
  <main
    class="page"
    role="main"
    aria-labelledby="outbox-title"
  >
    <header
      class="page-header"
      aria-labelledby="outbox-title"
    >
      <h1
        id="outbox-title"
        class="h1"
      >
        Postausgang
      </h1>
    </header>

    <section
      class="card"
      aria-busy="false"
    >
      <!-- Filterleiste wie bei Hinweise -->
      <div
        class="form"
        style="display:flex; align-items:flex-end; gap:12px; flex-wrap: wrap;"
      >
        <!-- Linker Block: Suchfeld + Selects in einer Zeile -->
        <div style="display:flex; align-items:flex-end; gap:8px; width:100%;">
          <div
            class="field"
            style="min-width:240px; flex:1 1 auto"
          >
            <label for="q">Filter</label>
            <input
              id="q"
              v-model="q"
              type="text"
              placeholder="Schüler, Absender, Betreff oder Text"
              @input="applyFilter"
            >
          </div>

          <div
            class="field"
            style="min-width:180px; flex:0 0 180px"
          >
            <label for="type">Typ</label>
            <select
              id="type"
              v-model="selectedType"
              @change="applyFilter"
            >
              <option value="">
                Alle
              </option>
              <option
                v-for="opt in availableTypes"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>

          <div
            class="field"
            style="min-width:220px; flex:0 0 220px"
          >
            <label for="sender">Absender</label>
            <select
              id="sender"
              v-model="selectedSender"
              @change="applyFilter"
            >
              <option value="">
                Alle
              </option>
              <option
                v-for="s in availableSenders"
                :key="s"
                :value="s"
              >
                {{ s }}
              </option>
            </select>
          </div>

          <div
            class="field"
            style="min-width:180px; flex:0 0 180px"
          >
            <label for="status">Status</label>
            <select
              id="status"
              v-model="selectedStatus"
              @change="applyFilter"
            >
              <option value="">
                Alle
              </option>
              <option
                v-for="opt in availableStatuses"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>
          <div
            class="field"
            style="margin-left:auto;"
          >
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!q && !selectedType && !selectedSender && !selectedStatus"
              aria-label="Filter zurücksetzen"
              @click="resetFilter"
            >
              Reset
            </button>
          </div>
        </div>

        <!-- Rechter Block: Reset-Button rechtsbündig -->
      </div>

      <div
        v-if="error"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ error }}
      </div>
      <div
        v-if="loading"
        class="mt-12"
      >
        Lade…
      </div>

      <table
        v-else
        class="compact-table mt-12"
        style="width:100%; border-collapse: collapse;"
      >
        <thead>
          <tr class="table-rows">
            <th class="muted">
              ID
            </th>
            <th class="muted">
              <span
                class="sortable"
                role="button"
                tabindex="0"
                @click="setSort('studentName')"
                @keydown.enter="setSort('studentName')"
              >
                Schüler
                <span v-if="sortKey === 'studentName'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
              </span>
            </th>
            <th class="muted">
              <span
                class="sortable"
                role="button"
                tabindex="0"
                @click="setSort('senderName')"
                @keydown.enter="setSort('senderName')"
              >
                Absender
                <span v-if="sortKey === 'senderName'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
              </span>
            </th>
            <th class="muted">
              Typ
            </th>
            <th class="muted">
              <span
                class="sortable"
                role="button"
                tabindex="0"
                @click="setSort('subject')"
                @keydown.enter="setSort('subject')"
              >
                Betreff
                <span v-if="sortKey === 'subject'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
              </span>
            </th>
            <th class="muted">
              Status
            </th>
            <th class="muted">
              <span
                class="sortable"
                role="button"
                tabindex="0"
                @click="setSort('sentAt')"
                @keydown.enter="setSort('sentAt')"
              >
                gesendet
                <span v-if="sortKey === 'sentAt'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
              </span>
            </th>
            <th
              class="muted"
              style="text-align: right;"
            >
              Aktion
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in pageItems"
            :key="row.id"
            class="table-rows"
          >
            <td>{{ row.id }}</td>
            <td>{{ row.studentName || '—' }}</td>
            <td>{{ row.senderName || '—' }}</td>
            <td>{{ labelType(row.type) }}</td>
            <td :title="row.subject">
              {{ truncate(row.subject, 60) }}
            </td>
            <td>
              <span :class="['chip', statusClass(row.status)]">{{ labelStatus(row.status) }}</span>
            </td>
            <td>
              <span :title="row.sentAt || ''">{{ formatDate(row.sentAt) }}</span>
            </td>
            <td style=" text-align:right">
              <button
                class="icon-btn"
                title="Details ansehen"
                @click="openDetails(row)"
              >
                <i
                  class="pi pi-eye"
                  aria-hidden="true"
                />
              </button>
            </td>
          </tr>
          <tr v-if="pageItems.length === 0">
            <td
              class="muted"
              colspan="8"
            >
              Keine Einträge vorhanden.
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination (analog Hinweise) -->
      <div
        class="mt-12"
        style="display:flex;align-items:center;gap:8px;justify-content:flex-end;"
      >
        <span
          class="muted"
          style="margin-right:auto"
        >Gesamt: {{ filteredCount }}</span>
        <label
          class="muted"
          for="outbox-limit"
        >pro Seite:</label>
        <select
          id="outbox-limit"
          :value="limit"
          @change="changeLimit"
        >
          <option
            v-for="n in [10,20,50,100]"
            :key="n"
            :value="n"
          >
            {{ n }}
          </option>
        </select>
        <button
          class="btn"
          :disabled="page<=1"
          @click="prevPage"
        >
          Zurück
        </button>
        <span class="muted">Seite {{ page }} / {{ pages || 1 }}</span>
        <button
          class="btn"
          :disabled="page>=pages"
          @click="nextPage"
        >
          Weiter
        </button>
      </div>
    </section>

    <!-- Dialog -->
    <BaseDialog
      :open="dialogOpen"
      :body-max-height="'60vh'"
      @close="closeDialog"
    >
      <template #title>
        {{ selected?.subject || 'Details' }}
      </template>

      <dl class="meta-grid">
        <div>
          <dt>Schüler</dt>
          <dd>{{ selected?.studentName || '—' }}</dd>
        </div>
        <div>
          <dt>Absender</dt>
          <dd>{{ selected?.senderName || '—' }}</dd>
        </div>
        <div>
          <dt>Typ</dt>
          <dd>{{ labelType(selected?.type) }}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd><span :class="['chip', statusClass(selected?.status)]">{{ labelStatus(selected?.status) }}</span></dd>
        </div>
        <div>
          <dt>Gesendet</dt>
          <dd>{{ formatDate(selected?.sentAt) }}</dd>
        </div>
      </dl>

      <div class="group">
        <h3 class="h3">
          Empfänger
        </h3>
        <ul class="list">
          <li
            v-for="(r, idx) in (selected?.recipients || [])"
            :key="idx"
          >
            {{ r.email }} <span
              v-if="r.name"
              class="muted"
            >({{ r.name }})</span>
          </li>
          <li
            v-if="!selected?.recipients || selected?.recipients.length === 0"
            class="muted"
          >
            —
          </li>
        </ul>
      </div>

      <div class="group">
        <h3 class="h3">
          Nachricht
        </h3>
        <pre class="message">{{ selected?.message }}</pre>
      </div>

      <p
        v-if="selected?.error"
        class="notice notice-danger"
        role="alert"
      >
        Fehler: {{ selected?.error }}
      </p>

      <template #footer>
        <button
          class="btn btn-primary"
          @click="closeDialog"
        >
          Schließen
        </button>
      </template>
    </BaseDialog>
  </main>
</template>

<script>
import api from '@/services/api';
import BaseDialog from '@/components/BaseDialog.vue';

export default {
  name: 'Outbox',
  components: {BaseDialog},
  data() {
    return {
      items: [],
      loading: false,
      error: null,
      dialogOpen: false,
      selected: null,

      // Filter & Sort
      q: '',
      selectedType: '',
      selectedSender: '',
      selectedStatus: '',
      sortKey: 'sentAt',
      sortDir: 'desc',

      // Pagination
      page: 1,
      limit: 20
    };
  },
  computed: {
    availableTypes() {
      const set = new Set((this.items || []).map(it => it.type).filter(Boolean));
      const list = Array.from(set).map(v => ({value: v, label: this.labelType(v)}));
      return list.sort((a, b) => String(a.label).localeCompare(String(b.label), 'de', {sensitivity: 'base'}));
    },
    availableSenders() {
      const set = new Set((this.items || []).map(it => it.senderName || '').filter(Boolean));
      return Array.from(set).sort((a, b) => String(a).localeCompare(String(b), 'de', {sensitivity: 'base'}));
    },
    availableStatuses() {
      const set = new Set((this.items || []).map(it => it.status || '').filter(Boolean));
      const list = Array.from(set).map(v => ({value: v, label: this.labelStatus(v)}));
      return list.sort((a, b) => String(a.label).localeCompare(String(b.label), 'de', {sensitivity: 'base'}));
    },
    filtered() {
      const query = String(this.q || '').trim().toLowerCase();
      return this.items.filter(it => {
        const matchesText = !query || [
          it.studentName || '',
          it.senderName || '',
          it.subject || '',
          it.message || '',
          it.type || ''
        ].join(' ').toLowerCase().includes(query);

        const matchesType = !this.selectedType || (it.type || '') === this.selectedType;
        const matchesSender = !this.selectedSender || (it.senderName || '') === this.selectedSender;
        const matchesStatus = !this.selectedStatus || (it.status || '') === this.selectedStatus;

        return matchesText && matchesType && matchesSender && matchesStatus;
      });
    },
    sorted() {
      const arr = [...this.filtered];
      const key = this.sortKey;
      const dir = this.sortDir;
      arr.sort((a, b) => {
        let va, vb;
        switch (key) {
          case 'studentName':
            va = String(a.studentName || '');
            vb = String(b.studentName || '');
            break;
          case 'senderName':
            va = String(a.senderName || '');
            vb = String(b.senderName || '');
            break;
          case 'subject':
            va = String(a.subject || '');
            vb = String(b.subject || '');
            break;
          case 'sentAt':
          default:
            va = a.sentAt ? new Date(a.sentAt).getTime() : 0;
            vb = b.sentAt ? new Date(b.sentAt).getTime() : 0;
            break;
        }
        let result;
        if (typeof va === 'number' && typeof vb === 'number') {
          result = va - vb;
        } else {
          result = String(va).localeCompare(String(vb), 'de', {sensitivity: 'base'});
        }
        return dir === 'asc' ? result : -result;
      });
      return arr;
    },
    pages() {
      const count = this.sorted.length;
      return Math.max(1, Math.ceil((count || 0) / this.limit));
    },
    pageItems() {
      const start = (this.page - 1) * this.limit;
      return this.sorted.slice(start, start + this.limit);
    },
    filteredCount() {
      return this.sorted.length;
    }
  },
  async created() {
    await this.fetchMessages();
  },
  methods: {
    async fetchMessages() {
      this.loading = true;
      this.error = null;
      try {
        // Großzügiger Pull, Filter/Sort/Paging lokal
        const {data} = await api.get('/communications/messages', {params: {limit: 500, offset: 0}});
        const rows = data?.items || data || [];
        this.items = rows.map(this.normalizeRow);
        // Auf erste Seite zurücksetzen
        this.page = 1;
      } catch (e) {
        this.error = (e && e.message) ? e.message : 'Fehler beim Laden.';
      } finally {
        this.loading = false;
      }
    },
    normalizeRow(r) {
      return {
        id: Number(r.id),
        studentId: r.studentId ?? r.student_id ?? null,
        studentName: r.studentName ?? r.student_name ?? null,
        senderUserId: r.senderUserId ?? r.sender_user_id ?? null,
        senderName: r.senderName ?? r.sender_name ?? null,
        type: r.type,
        subject: r.subject,
        status: r.status,
        sentAt: r.sentAt ?? r.sent_at ?? null,
        createdAt: r.createdAt ?? r.created_at ?? null,
        recipients: r.recipients ?? [],
        message: r.message ?? '',
        error: r.error ?? r.error_message ?? null
      };
    },
    openDetails(row) {
      this.selected = row;
      this.dialogOpen = true;
    },
    closeDialog() {
      this.dialogOpen = false;
      this.selected = null;
    },
    applyFilter() {
      this.page = 1;
    },
    resetFilter() {
      this.q = '';
      this.selectedType = '';
      this.selectedSender = '';
      this.selectedStatus = '';
      this.applyFilter();
    },
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        // Datum standardmäßig absteigend; Textspalten aufsteigend
        this.sortDir = key === 'sentAt' ? 'desc' : 'asc';
      }
    },
    nextPage() {
      if (this.page < this.pages) {
        this.page += 1;
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },
    changeLimit(e) {
      const val = Number(e?.target?.value || this.limit);
      this.limit = val > 0 ? val : 20;
      this.page = 1;
    },
    truncate(text, max = 60) {
      if (text == null) return '';
      const str = String(text);
      const limit = Math.max(1, Number(max) || 60);
      if (str.length <= limit) return str;
      return str.slice(0, limit - 1) + '…';
    },
    formatDate(iso) {
      if (!iso) return '–';
      try {
        const dt = new Date(iso);
        const df = new Intl.DateTimeFormat('de-DE', {
          timeZone: 'Europe/Berlin',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        const tf = new Intl.DateTimeFormat('de-DE', {
          timeZone: 'Europe/Berlin',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });

        return `${df.format(dt)} ${tf.format(dt)}`;
      } catch {
        return iso;
      }
    },
    labelType(code) {
      const c = String(code || '').toLowerCase();
      const map = {
        'absence_email': 'Abwesenheits-E‑Mail',
        'email': 'E‑Mail',
        'sms': 'SMS',
        'push': 'Push-Benachrichtigung',
        'notification': 'Benachrichtigung',
        'notice': 'Hinweis'
      };
      if (map[c]) return map[c];
      // Fallback: humanisieren
      const human = c.replace(/[_-]+/g, ' ').trim();
      return human ? human.charAt(0).toUpperCase() + human.slice(1) : '';
    },
    labelStatus(code) {
      const c = String(code || '').toLowerCase();
      const map = {
        'sent': 'gesendet',
        'failed': 'fehlgeschlagen',
        'queued': 'wartend',
        'pending': 'offen',
        'processing': 'in Bearbeitung',
        'draft': 'Entwurf',
        'delivered': 'zugestellt',
        'bounced': 'unzustellbar',
        'read': 'gelesen',
        'opened': 'geöffnet'
      };
      if (map[c]) return map[c];
      const human = c.replace(/[_-]+/g, ' ').trim();
      return human ? human.charAt(0).toUpperCase() + human.slice(1) : '';
    },
    statusClass(status) {
      const s = String(status || '').toLowerCase();
      if (s === 'sent') return 'chip-success';
      if (s === 'failed') return 'chip-danger';
      return 'chip-info';
    }
  }
};
</script>

<style scoped>
.page {
  padding: 16px;
  background: var(--bg);
  color: var(--text);
}

.page-header {
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
}

.btn {
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  cursor: pointer;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

.btn-primary:hover {
  background: var(--primary-600);
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  cursor: pointer;
}

.notice {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 10px 12px;
  background: var(--surface);
}

.notice-danger {
  background: rgba(239, 68, 68, .1);
  border-color: #fecaca;
}

.notice-success {
  background: rgba(16, 185, 129, .1);
  border-color: #bbf7d0;
}

.muted {
  color: var(--muted);
}

.mt-12 {
  margin-top: 12px;
}

.form .field label {
  font-size: 12px;
  color: var(--muted);
  display: block;
  margin-bottom: 4px;
}

.form .field input, .form .field textarea, .form .field select {
  width: 100%;
  height: 38px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
}

.form .field textarea {
  height: auto;
}

.form .field input:focus, .form .field textarea:focus, .form .field select:focus {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
}

.compact-table th, .compact-table td {
  font-size: 12px;
  line-height: 16px;
}

.compact-table td {
  font-size: 13px;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  text-decoration: underline;
}

.table-rows td, .table-rows th {
  padding: 3px 0;
  text-align: left;
  border-bottom: 1px solid var(--border)
}

.table-rows th {
  font-weight: 600;
}

/* Dialog (wie zuvor) */
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .35);
  display: grid;
  place-items: center;
  z-index: 1000;
}

.dialog {
  width: min(640px, calc(100vw - 32px));
  max-height: calc(100vh - 32px);
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, .22);
  display: flex;
  flex-direction: column;
}

.dialog-header,
.dialog-footer {
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
}

.dialog-footer {
  border-top: 1px solid var(--border);
  border-bottom: 0;
}

.dialog-body {
  padding: 12px 14px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dialog-body h3 {
  margin: 10px 0 5px;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px 12px;
}

.meta-grid dt {
  font-size: 12px;
  color: var(--muted);
}

.meta-grid dd {
  margin: 0;
}

.group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.list {
  margin: 0;
  padding-left: 16px;
}

.message {
  margin: 0;
  white-space: pre-wrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 13px;
  line-height: 1.45;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 10px;
}
</style>
